import ThumbImg from "../media/ThumbImg";
import { readableNameColor } from "../../lib/nameColor";
import { useState, type ReactNode } from 'react';

// The no-channel landing view: greeting hero, friend strips, summary stats, the
// 'find a space' / getting-started nudges, and the favorites-widget grid.
// Structural subsets of ChatLayout's types (ground rule 5: no back-imports).
type Space = { id: string; name?: string | null };
type DmFriend = { id: string; name?: string; username?: string };
type LandingFriend = { id: string; name?: string; username?: string; avatarUrl?: string | null; nameColor?: string | null; status?: string | null };
type Me = { name?: string | null; avatarUrl?: string | null; userId?: string };
type SummaryStat = { label: string; value: number };

type LandingDashboardProps = {
  isMdUp: boolean;
  channelPaneView: 'spaces' | 'dms';
  // Mobile DM card
  activeDmVoids: Space[];
  dmFriendSearch: string;
  setDmFriendSearch: (value: string) => void;
  filteredFriendsForDmSearch: DmFriend[];
  dmStartBusyUserId: string | null;
  unread: Record<string, number>;
  getDmDisplayName: (space?: { id?: string; name?: string | null } | null, fallbackId?: string) => string;
  // Hero + stats
  landingDateLabel: string;
  landingGreeting: string;
  me: Me;
  userName?: string | null;
  userStatusMessage: string;
  friendReqCount: number;
  landingSummaryStats: SummaryStat[];
  landingSearchPanel: ReactNode;
  // Friend strips
  friendsOnline: { id: string }[];
  mobileFriendSlots: LandingFriend[];
  globalUserIds: string[];
  presenceFromStatus: (status?: string | null) => 'online' | 'idle' | 'dnd';
  // Getting-started + favorites
  nonDmVoids: { id: string }[];
  landingFavoriteCards: ReactNode[];
  // Callbacks
  onStartDm: (userId: string) => void | Promise<void>;
  onOpenDm: (voidId: string) => void;
  onOpenProfile: (userId?: string) => void;
  onOpenFriends: () => void;
  onCreateSpace: () => void;
  /** Saves a new status line; when omitted the status is shown but not editable. */
  onUpdateStatus?: (next: string) => void | Promise<unknown>;
};

/** The status line under the greeting: tap to edit in place, Enter or blur saves, Escape cancels. */
function StatusLine({ value, placeholder, onSave, className }: { value: string; placeholder: string; onSave?: (next: string) => void | Promise<unknown>; className: string }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  if (!onSave) return <p className={className}>{value || placeholder}</p>;
  if (editing) {
    const commit = () => { setEditing(false); if (draft.trim() !== value.trim()) void onSave(draft); };
    return (
      <input
        autoFocus
        value={draft}
        maxLength={160}
        aria-label="Your status"
        placeholder={placeholder}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === 'Enter') commit(); else if (e.key === 'Escape') setEditing(false); }}
        className="surface-inset w-full max-w-xl rounded-lg border border-transparent px-2 py-1 text-sm text-fg outline-none placeholder:text-fg-3 focus:border-emerald-500/60"
      />
    );
  }
  return (
    <button type="button" onClick={() => { setDraft(value); setEditing(true); }} title="Edit your status" className={`${className} text-left hover:text-fg transition-colors`}>
      {value || placeholder}
    </button>
  );
}

function LandingDashboard({
  isMdUp,
  channelPaneView,
  activeDmVoids,
  dmFriendSearch,
  setDmFriendSearch,
  filteredFriendsForDmSearch,
  dmStartBusyUserId,
  unread,
  getDmDisplayName,
  landingDateLabel,
  landingGreeting,
  me,
  userName,
  userStatusMessage,
  friendReqCount,
  landingSummaryStats,
  landingSearchPanel,
  friendsOnline,
  mobileFriendSlots,
  globalUserIds,
  presenceFromStatus,
  nonDmVoids,
  landingFavoriteCards,
  onStartDm,
  onOpenDm,
  onOpenProfile,
  onOpenFriends,
  onCreateSpace,
  onUpdateStatus,
}: LandingDashboardProps) {
  const gettingStarted = nonDmVoids.length === 0 ? (
    <div className="rounded-2xl border border-emerald-700/30 bg-emerald-500/8 p-4 space-y-3">
                          <div className="text-sm font-medium text-emerald-200">Get started with Echo</div>
                          <p className="text-xs text-fg-3 leading-relaxed">
                            Spaces are private communities for your friends, family, or team. Create one or join an existing Space with an invite link.
                          </p>
                          <div className="flex flex-wrap gap-2">
                            <button
                              onClick={onCreateSpace}
                              className="rounded-lg bg-emerald-500/20 border border-emerald-600/40 px-3 py-1.5 text-xs font-medium text-emerald-200 hover:bg-emerald-500/30 transition-colors"
                            >
                              + Create a Space
                            </button>
                            <button
                              onClick={onCreateSpace}
                              className="rounded-lg border border-white/10 px-3 py-1.5 text-xs font-medium text-fg-2 hover:border-white/20 hover:text-fg-2 transition-colors"
                            >
                              Join with invite code
                            </button>
                          </div>
                        </div>
  ) : null;

  return (

            <div className="h-full w-full overflow-x-hidden">
              <div className="mx-auto max-w-screen-xl w-full px-4 sm:px-6 lg:px-8">
                  <div className="flex w-full flex-col gap-4 py-0 sm:gap-10 sm:py-6 sm:min-h-[calc(100vh-140px)]">
                    <section className="px-4 space-y-4 sm:space-y-5 flex flex-col flex-1 w-full">
                    {!isMdUp && channelPaneView === 'dms' && (
                      <div className="rounded-2xl border border-white/10 bg-black/25 p-4 space-y-3">
                        <div className="text-meta text-fg-2">Active direct messages</div>
                        {activeDmVoids.length === 0 ? (
                          <div className="rounded-2xl border border-white/10 bg-white/5 p-3 space-y-2">
                            <input
                              value={dmFriendSearch}
                              onChange={(e) => setDmFriendSearch(e.target.value)}
                              placeholder="Search friends to message..."
                              aria-label="Search friends to message"
                              className="w-full rounded-xl border border-white/10 bg-black/30 px-3 py-2 text-sm text-white outline-none placeholder:text-fg-3 focus:border-emerald-500/60"
                            />
                            <div className="space-y-1.5 max-h-64 overflow-auto">
                              {filteredFriendsForDmSearch.map((f) => (
                                <button
                                  key={`dm-search-mobile-card-${f.id}`}
                                  className="w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-left text-sm text-fg hover:border-emerald-500/40 disabled:opacity-60"
                                  disabled={dmStartBusyUserId === f.id}
                                  onClick={() => { void onStartDm(f.id); }}
                                >
                                  {f.name || f.username || 'Friend'}
                                </button>
                              ))}
                              {filteredFriendsForDmSearch.length === 0 && (
                                <div className="rounded-xl border border-white/5 bg-black/20 px-3 py-2 text-xs text-fg-3">
                                  {dmFriendSearch.trim() ? 'No friends match that search.' : 'No friends available yet.'}
                                </div>
                              )}
                            </div>
                          </div>
                        ) : (
                          <ul className="space-y-2">
                            {activeDmVoids.map((v) => {
                              const count = unread[`${v.id}:chat`] || 0;
                              return (
                                <li key={`mobile-dm-list-${v.id}`} className="flex items-center gap-2">
                                  <button
                                    className="flex-1 rounded-2xl border border-white/10 bg-white/5 px-3 py-2 text-left text-sm text-fg hover:border-emerald-500/50"
                                    onClick={() => onOpenDm(v.id)}
                                  >
                                    {getDmDisplayName(v, v.id)}
                                  </button>
                                  {count > 0 && (
                                    <span className="min-w-6 rounded-full bg-emerald-600 px-2 py-1 text-center text-xs font-semibold text-white">{count > 99 ? '99+' : count}</span>
                                  )}
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </div>
                    )}
                    <div className="space-y-5 sm:hidden">
                    <div className="relative rounded-2xl border border-white/10 bg-white/[0.04] p-5 ">
                      <div className="flex items-center gap-4 flex-wrap">
                        <div className="min-w-0 space-y-1">
                          <p className="text-meta text-fg-2 break-words [overflow-wrap:anywhere]">{landingDateLabel}</p>
                          <h1 className="mt-2 text-xl font-medium text-white break-words [overflow-wrap:anywhere]">{landingGreeting}, {me?.name || userName || 'Explorer'}</h1>
                          <StatusLine value={userStatusMessage} placeholder="Share what you are focusing on today." onSave={onUpdateStatus} className="mt-1 block text-xs text-fg-2 break-words [overflow-wrap:anywhere]" />
                        </div>
                        <button
                          className="h-16 w-16 flex-none overflow-hidden rounded-full border border-white/20 shadow-inner shadow-black/40"
                          onClick={() => onOpenProfile(me?.userId)}
                          aria-label="Open profile"
                        >
                          {me?.avatarUrl ? (
                            <ThumbImg w={56} src={me.avatarUrl} alt="" className="h-full w-full object-cover" />
                          ) : (
                            <div className="flex h-full w-full items-center justify-center text-2xl font-medium text-fg-2">
                              {(me?.name?.[0] || userName?.[0] || '?').toUpperCase()}
                            </div>
                          )}
                        </button>
                        <button
                          className="absolute top-3 right-3 inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-white/5 text-fg-2 hover:text-white hover:bg-white/10 transition"
                          onClick={onOpenFriends}
                          aria-label="Open friends modal"
                          title="Friends"
                        >
                          {friendReqCount > 0 && (
                            <span className="absolute -top-1 -right-1 min-w-[14px] px-1 py-0.5 rounded-full bg-emerald-500 text-meta font-semibold text-black ring-2 ring-neutral-900 text-center leading-none">
                              {Math.min(99, friendReqCount)}
                            </span>
                          )}
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
                            <circle cx="9" cy="7" r="4" />
                            <path d="M17 11a4 4 0 1 1-4 4" />
                            <path d="M3 21v-2a4 4 0 0 1 4-4h4" />
                            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                          </svg>
                        </button>
                      </div>
                        <div className="mt-6 grid grid-cols-2 gap-3">
                          {landingSummaryStats.map((stat) => (
                            <div key={stat.label} className="relative rounded-2xl border border-white/15 bg-black/20 px-2.5 py-3 text-center flex flex-col items-center">
                              <div className="text-lg font-medium text-white break-words [overflow-wrap:anywhere]">{stat.value}</div>
                              <div className="mt-1 text-meta text-fg-3 whitespace-nowrap leading-4">{stat.label}</div>
                                                          </div>
                          ))}
                        </div>
                        {landingSearchPanel && <div className="mt-4">{landingSearchPanel}</div>}
                        {gettingStarted && <div className="mt-4">{gettingStarted}</div>}
                    </div>
                    {friendsOnline.length > 0 && (
                      <div className="rounded-2xl p-2">
                        <div className="flex justify-center gap-2 overflow-x-auto px-1 pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                          {mobileFriendSlots.map((friend) => {
                            const isOnline = globalUserIds.includes(friend.id);
                            const presence = isOnline ? presenceFromStatus(friend.status) : 'offline';
                            const presenceDot =
                              presence === 'dnd' ? 'bg-red-400' :
                              presence === 'idle' ? 'bg-amber-400' :
                              'bg-online-light';
                            const presenceGlow = 'ring-1 ring-white/15';
                            return (
                              <button
                                key={`friend-dashboard-${friend.id}`}
                                className={`relative flex h-14 w-14 flex-none items-center justify-center overflow-hidden rounded-full border border-transparent bg-transparent ${presenceGlow}`}
                                aria-label={`${friend.name || friend.username || 'Friend'} - ${presence}`}
                                onClick={() => onOpenProfile(friend.id)}
                              >
                                {friend.avatarUrl ? (
                                  <ThumbImg w={56} src={friend.avatarUrl} alt="" className="h-full w-full object-cover" />
                                ) : (
                                  <span className="text-base font-medium text-fg">
                                    {(friend.name?.[0] || friend.username?.[0] || '?').toUpperCase()}
                                  </span>
                                )}
                                <span className={`absolute -bottom-1 -right-1 h-4 w-4 rounded-full ring-2 ring-neutral-900 ${presenceDot}`} />
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="hidden sm:grid grid-cols-1 xl:grid-cols-2 gap-4">
                    <div className="relative rounded-2xl border border-white/10 bg-white/[0.04] p-5 flex flex-col gap-5 ">
                      <div className="flex items-start gap-4">
                        <div className="h-20 w-20 rounded-full overflow-hidden border border-white/20 shadow-inner shadow-black/40">
                          {me?.avatarUrl ? (
                            <ThumbImg w={56} src={me.avatarUrl} alt={me?.name || 'Me'} className="h-full w-full object-cover" />
                          ) : (
                            <div className="flex h-full w-full items-center justify-center text-2xl font-medium text-fg-2">
                              {(me?.name?.[0] || userName?.[0] || '?').toUpperCase()}
                            </div>
                          )}
                        </div>
                        <div className="flex flex-col gap-1 text-left min-w-0">
                          <p className="text-xs text-fg-2 break-words [overflow-wrap:anywhere]">{landingDateLabel}</p>
                          <h2 className="text-3xl font-medium text-white break-words [overflow-wrap:anywhere]">{landingGreeting}, {me?.name || userName || 'Explorer'}</h2>
                          <StatusLine value={userStatusMessage} placeholder="Share what you are working on today." onSave={onUpdateStatus} className="block text-base text-fg-2 max-w-xl break-words [overflow-wrap:anywhere]" />
                        </div>
                      </div>
                      <button
                        className="absolute top-4 right-4 inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-white/5 text-fg-2 hover:text-white hover:bg-white/10 transition"
                        onClick={onOpenFriends}
                        aria-label="Open friends modal"
                        title="Friends"
                      >
                        {friendReqCount > 0 && (
                          <span className="absolute -top-1 -right-1 min-w-[14px] px-1 py-0.5 rounded-full bg-emerald-500 text-meta font-semibold text-black ring-2 ring-neutral-900 text-center leading-none">
                            {Math.min(99, friendReqCount)}
                          </span>
                        )}
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
                          <circle cx="9" cy="7" r="4" />
                          <path d="M17 11a4 4 0 1 1-4 4" />
                          <path d="M3 21v-2a4 4 0 0 1 4-4h4" />
                          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                        </svg>
                      </button>
                      <div className="grid grid-cols-3 gap-3">
                        {landingSummaryStats.map((stat) => (
                          <div key={`desktop-stat-${stat.label}`} className="relative rounded-2xl border border-white/15 bg-white/5 px-3 py-3 text-center flex flex-col items-center">
                            <div className="text-2xl font-medium text-white break-words [overflow-wrap:anywhere]">{stat.value}</div>
                            <div className="mt-1 text-meta text-fg-3 break-words [overflow-wrap:anywhere]">{stat.label}</div>
                          </div>
                        ))}
                      </div>
                      {landingSearchPanel && <div className="mt-4">{landingSearchPanel}</div>}
                      {gettingStarted && <div className="mt-4">{gettingStarted}</div>}
                    </div>
                    <div className="rounded-2xl p-3">
                      <div className="flex flex-wrap gap-2 max-h-[140px] overflow-y-auto px-1 py-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden justify-center xl:justify-start">
                        {mobileFriendSlots.map((friend) => {
                          const isOnline = globalUserIds.includes(friend.id);
                          const presence = isOnline ? presenceFromStatus(friend.status) : 'offline';
                          const presenceDot =
                            presence === 'dnd' ? 'bg-red-400' :
                            presence === 'idle' ? 'bg-amber-400' :
                            'bg-online-light';
                          const presenceGlow = 'ring-1 ring-white/15';
                          return (
                            <button
                              key={`desktop-friend-${friend.id}`}
                              className="flex flex-col items-center gap-2 text-center w-auto min-w-[64px] max-w-[140px] border border-transparent bg-transparent p-0"
                              onClick={() => onOpenProfile(friend.id)}
                            >
                              <div className={`relative h-12 w-12 rounded-full overflow-hidden border border-white/15 ${presenceGlow}`}>
                                {friend.avatarUrl ? (
                                  <ThumbImg w={56} src={friend.avatarUrl} alt={friend.name || friend.username || 'Friend'} className="h-full w-full object-cover" />
                                ) : (
                                  <div className="flex h-full w-full items-center justify-center text-sm font-medium text-fg-2">
                                    {(friend.name?.[0] || friend.username?.[0] || '?').toUpperCase()}
                                  </div>
                                )}
                                <span className={`absolute -bottom-1 -right-1 h-3 w-3 rounded-full ring-2 ring-neutral-900 ${presenceDot}`} />
                              </div>
                              <span className="text-sm font-medium text-fg truncate" style={readableNameColor(friend.nameColor) ? { color: readableNameColor(friend.nameColor) } : undefined}>
                                {friend.name || friend.username || 'Friend'}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                  {landingFavoriteCards.length > 0 && (
                    <div className="flex flex-1 min-h-0 flex-col">
                      <div className="text-sm font-medium text-white sm:hidden">Your widgets</div>
                      <div className="mt-2 sm:mt-3 columns-1 sm:columns-2 xl:columns-3 gap-4 w-full min-w-0 px-2 pb-3 md:px-0">
                        {landingFavoriteCards.map((card, idx) => (
                          <div
                            key={'fav-' + idx}
                            className="mb-4 w-full min-w-0 break-inside-avoid"
                          >
                            <div className="group surface-glass min-h-[180px] md:min-h-[220px] rounded-2xl p-4">
                              {card}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </section>
              </div>
            </div>
          </div>

  );
}

export default LandingDashboard;
